import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { AppWrap, MotionWrap } from '../../wrapper';
import analyticsService from '../../services/analyticsService';
import './AIChat.scss';

const AIChat = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // Resume data - replace with your actual information
    const resumeData = `
        ### Dhrumit Patel - Data Scientist & Full Stack Developer

        **Contact Information:**
        - Email: dhrumitpatel.ai@gmail.com
        - LinkedIn: linkedin.com/in/dhrumitpatelds
        - GitHub: github.com/Dhrumit1314
        - Hugging Face: huggingface.co/Dhrumit1314

        **Summary:**
        Passionate Data Scientist and Full Stack Developer with expertise in machine learning, deep learning, and web development. Experienced in building scalable applications using modern technologies and frameworks. Strong background in Python, React, Node.js, and cloud platforms.

        **Skills:**
        - **Programming Languages:** Python, JavaScript, SQL, HTML/CSS
        - **Machine Learning:** TensorFlow, PyTorch, Scikit-learn, Keras, NumPy, Pandas
        - **Frontend:** React, Redux, Next.js, Tailwind CSS, Material-UI
        - **Backend:** Node.js, Express.js, Python, Django, Flask, RESTful APIs
        - **Databases:** MongoDB, PostgreSQL, MySQL, Firebase
        - **Cloud Platforms:** AWS, Google Cloud Platform, Microsoft Azure
        - **Tools & Technologies:** Git, Docker, Jupyter Notebooks, Power BI

        **Work Experience:**

        **Data Scientist** | Tech Company | Location
        *2022 – Present*
        - Developed machine learning models for predictive analytics
        - Implemented deep learning solutions using TensorFlow and PyTorch
        - Collaborated with cross-functional teams to deliver data-driven insights

        **Software Developer** | Another Company | Location
        *2020 – 2022*
        - Built full-stack web applications using React and Node.js
        - Integrated machine learning models into production systems
        - Optimized database queries and improved application performance

        **Education:**

        **Bachelor's Degree in Computer Science** | University Name | 2020
        - Specialization in Data Science and Machine Learning

        **Projects:**

        **1. AI-Powered Portfolio Agent**
        - **Description:** An intelligent chatbot for portfolio websites
        - **Technologies:** React, Google Gemini API, JavaScript, Tailwind CSS
        - **Features:** Real-time conversational AI, resume-based responses

        **2. Machine Learning Model Deployment**
        - **Description:** End-to-end ML pipeline for predictive modeling
        - **Technologies:** Python, TensorFlow, Flask, Docker, AWS
        - **Features:** Automated model training, API endpoints, monitoring

        **3. E-commerce Platform**
        - **Description:** Full-stack e-commerce application
        - **Technologies:** React, Node.js, MongoDB, Stripe API
        - **Features:** User authentication, payment processing, admin dashboard
    `;

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = async () => {
        if (!input.trim()) return;

        const userMessage = { role: 'user', parts: [{ text: input.trim() }] };
        setMessages((prevMessages) => [...prevMessages, userMessage]);
        
        // Track AI chat interaction
        analyticsService.trackAIChatInteraction(input.trim(), 'sent');
        
        setInput('');
        setIsLoading(true);

        try {
            const chatHistory = [
                { 
                    role: "user", 
                    parts: [{ 
                        text: `Here is my resume and portfolio data. Please use this information exclusively to answer questions about me. If you cannot find the answer in this data, state that you don't have that information. Do not make up answers.

IMPORTANT FORMATTING INSTRUCTIONS:
Always format your responses using proper Markdown syntax for optimal readability:

1. **Use clear section headers** with ### for main sections and #### for subsections
2. **Organize information logically** - group related items together
3. **Use bullet points** (• or -) for lists of items
4. **Highlight important information** with **bold text** for key terms, numbers, and achievements
5. **Use italics** for emphasis and descriptions
6. **Structure projects/repositories** by category (Web Development, Machine Learning, etc.)
7. **Include relevant metrics** like stars, downloads, performance scores
8. **Make it scannable** - use clear spacing and structure
9. **Use code formatting** for technical terms, languages, and tools
10. **Keep descriptions concise** but informative

Example format:
### 🚀 GitHub Projects

#### **Web Development**
• **Project Name** - *Brief description* - Built with \`React\`, \`Node.js\` - ⭐ **150** stars
• **Another Project** - *Description* - Technologies: \`TypeScript\`, \`MongoDB\`

#### **Machine Learning**
• **ML Project** - *Problem it solves* - \`TensorFlow\`, \`Python\` - Accuracy: **95%**

### 💼 Skills & Technologies

#### **Programming Languages**
• **Python** - Primary language for ML/AI projects
• **JavaScript/TypeScript** - Full-stack development

Please ensure all responses are well-structured, easy to read, and professional.\n\n${resumeData}` 
                    }] 
                },
                ...messages,
                userMessage,
            ];

            const payload = { contents: chatHistory };
            const apiKey = process.env.REACT_APP_GEMINI_API_KEY; // Add this to your .env file
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error("API Error:", errorData);
                setMessages((prevMessages) => [
                    ...prevMessages,
                    { role: 'model', parts: [{ text: 'Oops! Something went wrong while connecting to the AI. Please try again.' }] }
                ]);
            } else {
                const result = await response.json();
                if (result.candidates && result.candidates.length > 0 &&
                    result.candidates[0].content && result.candidates[0].content.parts &&
                    result.candidates[0].content.parts.length > 0) {
                    const aiResponseText = result.candidates[0].content.parts[0].text;
                    
                    // Track AI response
                    analyticsService.trackAIChatInteraction(input.trim(), 'received_response', aiResponseText.length);
                    
                    setMessages((prevMessages) => [
                        ...prevMessages,
                        { role: 'model', parts: [{ text: aiResponseText }] }
                    ]);
                } else {
                    setMessages((prevMessages) => [
                        ...prevMessages,
                        { role: 'model', parts: [{ text: 'I did not receive a valid response from the AI. Please try rephrasing your question.' }] }
                    ]);
                }
            }
        } catch (error) {
            console.error("Fetch Error:", error);
            setMessages((prevMessages) => [
                ...prevMessages,
                { role: 'model', parts: [{ text: 'An unexpected error occurred. Please check your network connection and try again.' }] }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isLoading) {
            sendMessage();
        }
    };

    return (
        <>
            <h2 className="head-text">
                AI Portfolio <span>Assistant</span>
            </h2>

            <div className="app__aichat-container">
                <div className="app__aichat-messages">
                    {messages.length === 0 && (
                        <div className="app__aichat-welcome">
                            <p className="p-text">Hello! I'm Dhrumit's AI assistant.</p>
                            <p className="p-text">Ask me anything about his skills, experience, or projects!</p>
                            <p className="p-text">For example: "What are Dhrumit's key skills?" or "Tell me about his projects."</p>
                        </div>
                    )}
                    {messages.map((msg, index) => (
                        <motion.div
                            key={index}
                            className={`app__aichat-message ${msg.role === 'user' ? 'user' : 'ai'}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="message-content">
                                <ReactMarkdown>{msg.parts[0].text}</ReactMarkdown>
                            </div>
                        </motion.div>
                    ))}
                    {isLoading && (
                        <motion.div
                            className="app__aichat-message ai"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="message-content">
                                <p className="p-text">Typing...</p>
                            </div>
                        </motion.div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <div className="app__aichat-input">
                    <input
                        type="text"
                        className="p-text"
                        placeholder="Ask me a question..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                    />
                    <button
                        className="app__aichat-send-btn"
                        onClick={sendMessage}
                        disabled={isLoading}
                    >
                        Send
                    </button>
                </div>
            </div>
        </>
    );
};

export default AppWrap(
    MotionWrap(AIChat, 'app__aichat'),
    'aichat',
    'app__primarybg'
); 